/** @format */
'use client';

import { cssVars } from '@atoms/GlobalStyles';
import { signIn, useSession } from 'next-auth/react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const StyledSessionOutOfSyncBanner = styled.div`
  background: ${cssVars.red};
  color: white;
  width: 100%;
  min-height: 48px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  text-align: center;
`;

const SignInAgainButton = styled.button`
  background: white;
  color: ${cssVars.red};
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  font-weight: bold;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;

/**
 * Non-dismissable banner shown when the session's granted permissions no longer cover what the app
 * requires (e.g. a new Graph scope was added since the user last signed in). Only this case needs
 * interactive re-authentication; stale profile data is self-healed silently without disrupting the user.
 */
function SessionOutOfSyncBanner() {
  const { data: session } = useSession();

  if (session?.error !== 'SessionError') {
    return null;
  }

  const handleSignInAgain = () => {
    signIn('microsoft-entra-id', { callbackUrl: typeof window !== 'undefined' ? window.location.href : undefined });
  };

  return (
    <StyledSessionOutOfSyncBanner>
      <span>
        <FormattedMessage
          defaultMessage="Your session is out of date and some features may not work. Please sign in again to continue."
          description="Banner message shown when the signed-in user's session is missing permissions the app now requires"
        />
      </span>
      <SignInAgainButton onClick={handleSignInAgain} type="button">
        <FormattedMessage defaultMessage="Sign in again" description="Button label to re-authenticate from the out-of-date session banner" />
      </SignInAgainButton>
    </StyledSessionOutOfSyncBanner>
  );
}

export default SessionOutOfSyncBanner;
